import { Controller } from "@hotwired/stimulus"

// Type-ahead column picker. Replaces a <select> that listed every source column
// as an <option>: on a wide layout that was N pickers x M columns = thousands of
// idle <option> nodes. Here the option list is rendered into `list` only while
// the dropdown is open, so the idle DOM holds just the search input + hidden
// value. The visible text is the column label ("3 - Name"); the submitted value
// is the column position, carried in a hidden field whose `change` drives the
// same autosave the old <select> did.
export default class extends Controller {
  static targets = ["search", "value", "list"]
  static values = { options: Array }

  initialize() {
    this.activeIndex = -1
    this.filtered = []
    this.onDocPointer = this.onDocPointer.bind(this)
  }

  connect() {
    document.addEventListener("mousedown", this.onDocPointer)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onDocPointer)
  }

  open() {
    if (this.searchTarget.disabled) return
    this.render(this.optionsValue)
    this.show()
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    const matches =
      q === ""
        ? this.optionsValue
        : this.optionsValue.filter((o) => String(o.label).toLowerCase().includes(q))
    this.render(matches)
    this.show()
  }

  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (!this.listTarget.hidden && this.activeIndex >= 0) {
          event.preventDefault()
          this.choose(this.activeIndex)
        }
        break
      case "Escape":
        this.hide()
        break
    }
  }

  blur() {
    // Defer so an option's mousedown handler can run first when focus leaves.
    setTimeout(() => this.hide(), 0)
  }

  render(options) {
    this.filtered = options
    this.activeIndex = -1
    this.listTarget.innerHTML = ""

    if (options.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-1.5 text-zinc-400 dark:text-zinc-500"
      empty.textContent = this.listTarget.dataset.emptyText || ""
      this.listTarget.appendChild(empty)
      return
    }

    options.forEach((option, index) => {
      const item = document.createElement("li")
      item.dataset.value = option.value
      item.dataset.index = index
      item.setAttribute("role", "option")
      item.setAttribute("data-test-id", "column-picker-option")
      item.className =
        "cursor-pointer px-3 py-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      item.textContent = option.label
      // mousedown (not click) so the choice lands before the search input blurs.
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.choose(index)
      })
      this.listTarget.appendChild(item)
    })
  }

  move(delta) {
    if (this.listTarget.hidden) this.filter()
    if (this.filtered.length === 0) return

    this.activeIndex = (this.activeIndex + delta + this.filtered.length) % this.filtered.length
    this.highlight()
  }

  highlight() {
    this.listTarget.querySelectorAll("[data-test-id='column-picker-option']").forEach((item, index) => {
      const on = index === this.activeIndex
      item.classList.toggle("bg-zinc-100", on)
      item.classList.toggle("dark:bg-zinc-800", on)
      if (on) item.scrollIntoView({ block: "nearest" })
    })
  }

  choose(index) {
    const option = this.filtered[index]
    if (!option) return

    this.searchTarget.value = option.label
    this.setValue(String(option.value))
    this.hide()
  }

  setValue(value) {
    if (this.valueTarget.value === value) return

    this.valueTarget.value = value
    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  onDocPointer(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  show() {
    this.listTarget.hidden = false
    this.searchTarget.setAttribute("aria-expanded", "true")
  }

  hide() {
    this.listTarget.hidden = true
    this.searchTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }
}
